import { getCompanyGetCompanyConfig, postCompanySaveCompany, postCompanySaveCompanyConfigs } from '@apis/Customers';
import { Company, CompanyConfiguration } from '@apis/Customers/model';
import { CompanyTenantPrereqService } from '@root/Components/Router/CompanyContent';
import { inject, injectable } from 'tsyringe';
import { BaseCacheByTenant } from './BaseCacheByTenant';
import { ICompanyContextToken } from './CompanyContext';

interface ICompanyConfigModel {
    MapWorkload?: 'Require';
    Export?: 'Block';
    ForecastMaxPredictionDays?: string;
    CurThrottling?: string;
    /**Not Stored in Company Config Service */
    SyncResources?: 'true' | 'false';
    /**Not Stored in Company Config Service */
    IsResale?: 'true' | 'false';
    FocusEnabled?: 'true' | 'false';
    AwsRoleNameOverride?: string;
    AwsExternalIdOverride?: string;
    /**
     * JSON serialized string array of invoice ingestion indexers (aka RollupTypes) that are enabled for the company.
     * If empty array, all are disabled.
     * If null, all are enabled.
     * Otherwise, it is the list of enabled rollup types.
     */
    EnabledRollupTypes?: string;
    ArcheraApiKey?: string;
    ArcheraOrgId?: string;
}

class CompanyConfig {
    private readonly model: ICompanyConfigModel;

    public constructor(public readonly configs: CompanyConfiguration[]) {
        this.model = this.configs.reduce((acc, c) => ({ ...acc, [c.Key ?? '']: c.Value }), {} as ICompanyConfigModel);
    }

    public get enabledRollupTypes() {
        return this.model.EnabledRollupTypes;
    }
    public get requireMapWorkload() {
        return this.checkConfig('MapWorkload', 'Require');
    }

    public get blockExport() {
        return this.checkConfig('Export', 'Block');
    }

    public get forecastMaxPredictionDays() {
        return this.model.ForecastMaxPredictionDays;
    }

    public get curThrottling() {
        return this.model.CurThrottling;
    }

    public get focusEnabled() {
        return this.checkConfig('FocusEnabled', 'true');
    }

    public get awsRoleNameOverride() {
        return this.model.AwsRoleNameOverride;
    }

    public get awsExternalIdOverride() {
        return this.model.AwsExternalIdOverride;
    }

    public get archeraApiKey() {
        return this.model.ArcheraApiKey;
    }

    public get archeraOrgId() {
        return this.model.ArcheraOrgId;
    }

    private checkConfig<TKey extends keyof ICompanyConfigModel>(key: TKey, value: string) {
        return this.model[key]?.localeCompare(value, undefined, { sensitivity: 'base' }) === 0;
    }
}

@injectable()
class CompanyConfigServiceCache extends BaseCacheByTenant<CompanyConfig> {
    public constructor(@inject(CompanyTenantPrereqService) tenantSvc: CompanyTenantPrereqService) {
        super(tenantSvc);
    }
}

@injectable()
export class CompanyConfigService {
    public constructor(
        @inject(CompanyConfigServiceCache) private readonly cache: CompanyConfigServiceCache,
        @inject(ICompanyContextToken) private readonly company: Company
    ) {}

    public async getTypedCompanyConfig() {
        return this.cache.get(this.company.Id ?? 0, async () => {
            const configs = await getCompanyGetCompanyConfig();
            return new CompanyConfig(configs);
        });
    }

    public async saveCompanyConfigs(configs: CompanyConfiguration[]) {
        await postCompanySaveCompanyConfigs(configs);
        this.cache.invalidate(this.company.Id ?? 0);
    }
}
